// Members and invitations of the active organisation (GIOTHUB-186):
// everyone sees the members; admins invite, change roles, remove and
// withdraw invitations; anyone may leave.
import { useEffect, useState } from "preact/hooks";
import { api } from "../lib/api";
import { setActiveOrg } from "../lib/org";
import type { Invitation, InviteResult, MemberRow, Role } from "../lib/types";
import { Centered } from "../App";
import { fmtDateTime, lang, LANG_NAMES, LANGS, t, useLang, type Lang } from "../i18n";
import { NoOrg, useMe } from "./Projects";
import { roleName, Shell } from "./Shell";

const ROLES: Role[] = ["admin", "builder", "viewer"];

export function Members() {
  useLang();
  const [me, meErr] = useMe();
  const [members, setMembers] = useState<MemberRow[] | null>(null);
  const [invs, setInvs] = useState<Invitation[]>([]);
  const [err, setErr] = useState("");
  const [note, setNote] = useState("");
  const [sent, setSent] = useState<InviteResult | null>(null);
  const [form, setForm] = useState<{ email: string; role: Role; lang: Lang }>({ email: "", role: "builder", lang: lang() });
  const admin = me?.role === "admin";
  const load = async () => {
    const m = await api<{ members: MemberRow[] }>("GET", "/members");
    setMembers(m.members);
    if (admin) setInvs((await api<{ invitations: Invitation[] }>("GET", "/invitations")).invitations);
  };
  useEffect(() => {
    if (me?.organisation) load().catch((e) => setErr(e.message));
  }, [me]);
  if (meErr) return <Centered>{meErr}</Centered>;
  if (!me) return <Centered>{t("common.loading")}</Centered>;
  if (!me.organisation) return <NoOrg me={me} />;
  const act = async (done: string, f: () => Promise<unknown>) => {
    setErr("");
    setNote("");
    try {
      await f();
      setNote(done);
      await load();
    } catch (x) {
      setErr((x as Error).message);
    }
  };
  const invite = async (e: Event) => {
    e.preventDefault();
    setErr("");
    setNote("");
    try {
      const r = await api<InviteResult>("POST", "/invitations", form);
      setSent(r);
      setForm({ ...form, email: "" });
      await load();
    } catch (x) {
      setErr((x as Error).message);
    }
  };
  const leave = async () => {
    if (!confirm(t("members.leave.confirm", { org: me.organisation!.name }))) return;
    try {
      await api("DELETE", `/members/${encodeURIComponent(me.user_id)}`);
      setActiveOrg("");
      location.href = "/";
    } catch (x) {
      setErr((x as Error).message);
    }
  };
  return (
    <Shell me={me} active="members">
      <main class="mx-auto grid w-full max-w-[1100px] flex-1 content-start gap-6 overflow-y-auto p-4 sm:p-8">
        <div class="flex flex-wrap items-end justify-between gap-3">
          <h1 class="text-[20px] font-semibold">{t("members.title", { org: me.organisation.name })}</h1>
          <button type="button" class="btn" onClick={leave}>{t("members.leave")}</button>
        </div>
        {err && <p class="rounded-lg border border-crit bg-crit-soft px-3 py-2 text-crit" role="alert">{err}</p>}
        {note && <p class="rounded-lg border border-good bg-good-soft px-3 py-2 text-good" role="status">{note}</p>}
        <section class="overflow-x-auto rounded-xl border border-line bg-side">
          <table class="w-full text-[13px]">
            <thead class="text-left text-[11px] uppercase tracking-wider text-muted">
              <tr><th class="px-4 py-2">{t("members.person")}</th><th class="px-4 py-2">{t("members.role")}</th><th class="px-4 py-2" /></tr>
            </thead>
            <tbody>
              {(members ?? []).map((m) => (
                <tr class="border-t border-line">
                  <td class="px-4 py-2">
                    <span class="font-medium">{m.name || m.email || m.user_id}</span>
                    {m.user_id === me.user_id && <span class="ml-1 text-muted">({t("members.you")})</span>}
                    {m.name && m.email && <span class="block text-[12px] text-muted">{m.email}</span>}
                  </td>
                  <td class="px-4 py-2">
                    {admin ? (
                      <select class="field !w-auto !py-1" value={m.role} aria-label={t("members.role")}
                        onChange={(e) => act(t("members.changed"), () => api("PATCH", `/members/${encodeURIComponent(m.user_id)}`, { role: (e.target as HTMLSelectElement).value }))}>
                        {ROLES.map((r) => <option value={r}>{roleName(r)}</option>)}
                      </select>
                    ) : roleName(m.role)}
                  </td>
                  <td class="px-4 py-2 text-right">
                    {admin && m.user_id !== me.user_id && (
                      <button type="button" class="btn btn-ghost text-crit"
                        onClick={() => confirm(t("members.remove.confirm", { who: m.name || m.email || m.user_id })) &&
                          act(t("members.removed"), () => api("DELETE", `/members/${encodeURIComponent(m.user_id)}`))}>
                        {t("members.remove")}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
        <p class="text-[12.5px] text-muted">{t("members.roles")}</p>
        {admin && (
          <section class="grid gap-4 lg:grid-cols-[minmax(0,1fr)_380px]">
            <div class="rounded-xl border border-line bg-side p-5">
              <h2 class="mb-3 text-[15px] font-semibold">{t("members.open")}</h2>
              {invs.length === 0 && <p class="text-muted">{t("members.open.none")}</p>}
              <ul class="grid gap-2">
                {invs.map((i) => (
                  <li class="flex flex-wrap items-center gap-x-3 gap-y-1">
                    <span class="font-medium">{i.email}</span>
                    <span class="chip">{roleName(i.role)}</span>
                    <span class="text-[12px] text-muted">{t("members.until", { date: fmtDateTime(i.expires_at) })}</span>
                    <span class="flex-1" />
                    <button type="button" class="btn btn-ghost" onClick={() => act(t("members.withdrawn"), () => api("DELETE", `/invitations/${i.id}`))}>
                      {t("members.withdraw")}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
            <form class="h-fit rounded-xl border border-line bg-side p-5" onSubmit={invite}>
              <h2 class="mb-3 text-[15px] font-semibold">{t("members.invite")}</h2>
              <label class="mb-1 block text-[12px] font-semibold text-ink2" for="inv-email">{t("members.email")}</label>
              <input id="inv-email" class="field mb-3" type="email" required value={form.email} placeholder="name@company.com"
                onInput={(e) => setForm({ ...form, email: (e.target as HTMLInputElement).value })} />
              <div class="mb-4 grid grid-cols-2 gap-3">
                <label class="text-[12px] font-semibold text-ink2">{t("members.role")}
                  <select class="field mt-1" value={form.role} onChange={(e) => setForm({ ...form, role: (e.target as HTMLSelectElement).value as Role })}>
                    {ROLES.map((r) => <option value={r}>{roleName(r)}</option>)}
                  </select>
                </label>
                <label class="text-[12px] font-semibold text-ink2">{t("members.lang")}
                  <select class="field mt-1" value={form.lang} onChange={(e) => setForm({ ...form, lang: (e.target as HTMLSelectElement).value as Lang })}>
                    {LANGS.map((l) => <option value={l}>{LANG_NAMES[l]}</option>)}
                  </select>
                </label>
              </div>
              <button type="submit" class="btn btn-pri w-full justify-center">{t("members.send")}</button>
              {sent && <InviteLink r={sent} />}
            </form>
          </section>
        )}
      </main>
    </Shell>
  );
}

/** The link of a new invitation, with what happened to the e-mail. */
export function InviteLink({ r }: { r: InviteResult }) {
  return (
    <div class="mt-4 grid gap-2 rounded-lg border border-line bg-raised p-3 text-[12.5px]" role="status">
      <span class={r.mail_sent ? "text-good" : "text-warn"}>{r.mail_sent ? t("members.mail.sent", { email: r.invitation.email }) : r.mail_error || t("members.mail.not")}</span>
      {r.account === "created" && <span class="text-muted">{t("members.account.created")}</span>}
      {r.account === "unknown" && <span class="text-warn">{t("members.account.unknown")}</span>}
      <span class="text-muted">{t("members.link")}</span>
      <div class="flex gap-2">
        <input class="field font-mono text-[11.5px]" readOnly value={r.invitation_url} onFocus={(e) => (e.target as HTMLInputElement).select()} />
        <button type="button" class="btn" onClick={() => navigator.clipboard?.writeText(r.invitation_url)}>{t("common.copy")}</button>
      </div>
    </div>
  );
}
